'use client';

import {useId} from 'react';
import {useI18n} from '@/lib/i18n/client';
import Modal from './Modal';

interface IdleNotificationProps {
    open: boolean;
    onTap?: () => void;
}

/**
 * Idle-timeout warning shown after user inactivity. Composes the shared
 * `Modal` primitive but suppresses the X button (`showCloseButton={false}`)
 * because the entire card is itself a tap-to-dismiss target — any touch
 * anywhere on the kiosk also bubbles up to `useIdleTimeout`'s window-level
 * activity listener, which cancels the timeout. The card-level `onClick`
 * just guarantees `onTap` runs even when callers wire it explicitly.
 */
export default function IdleNotification({
    open,
    onTap,
}: IdleNotificationProps) {
    const {t} = useI18n();
    const titleId = useId();

    function handleTap() {
        onTap?.();
    }

    return (
        <Modal
            open={open}
            onClose={handleTap}
            titleId={titleId}
            showCloseButton={false}
            zIndex={70}
        >
            <div onClick={handleTap} className="cursor-pointer text-center">
                <h2
                    id={titleId}
                    className="text-display-lg text-phaethon-text mb-12"
                >
                    {t('idle.title')}
                </h2>
                <p className="text-body-lg text-phaethon-text mb-8">
                    {t('idle.tapToContinue')}
                </p>
                <p className="text-body-lg text-phaethon-gray-medium">
                    {t('idle.timeoutWarning')}
                </p>
            </div>
        </Modal>
    );
}
